import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useUserList } from "@/modules/users/hooks/useUsers";
import { useProductList } from "@/modules/products/hooks/useProducts";
import {
  useCreateEnquiry,
  useEnquiryCategories,
  useEnquiryPriorities,
  useEnquirySources,
} from "../hooks/useEnquiries";
import type { EnquiryDetail } from "../types";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (enquiry: EnquiryDetail) => void;
};

export function NewEnquiryDrawer({ isOpen, onClose, onCreated }: Props) {
  const createEnquiry = useCreateEnquiry();
  const { data: sources = [] } = useEnquirySources();
  const { data: priorities = [] } = useEnquiryPriorities();
  const { data: categories = [] } = useEnquiryCategories();
  const users = useUserList({ limit: 200 });
  const products = useProductList({ limit: 200 });

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [priority, setPriority] = useState("medium");
  const [category, setCategory] = useState("");
  const [source, setSource] = useState("manual");
  const [assignedToId, setAssignedToId] = useState("");
  const [linkedProductId, setLinkedProductId] = useState("");

  function reset() {
    setName("");
    setEmail("");
    setPhone("");
    setCompanyName("");
    setJobTitle("");
    setSubject("");
    setMessage("");
    setPriority("medium");
    setCategory("");
    setSource("manual");
    setAssignedToId("");
    setLinkedProductId("");
  }

  useEffect(() => {
    if (isOpen) reset();
  }, [isOpen]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!subject.trim() && !message.trim() && !name.trim()) return;
    const res = await createEnquiry.mutateAsync({
      source: source || "manual",
      status: "new",
      name: name.trim() || undefined,
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      companyName: companyName.trim() || undefined,
      jobTitle: jobTitle.trim() || undefined,
      subject: subject.trim() || undefined,
      message: message.trim() || undefined,
      priority,
      category: category || undefined,
      assignedToId: assignedToId || null,
      linkedProductId: linkedProductId || null,
    });
    onCreated?.(res.enquiry);
    reset();
    onClose();
  }

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/30" onClick={onClose} />
      <div
        className="fixed right-0 top-0 z-[9999] flex h-full w-[500px] flex-col border-l bg-white shadow-xl"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <h2 className="text-lg font-semibold text-foreground">Add Enquiry</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-1 flex-col overflow-y-auto"
        >
          <div className="flex-1 space-y-6 p-6">
            <h3 className="text-sm font-semibold text-foreground">Submitter</h3>
            <div className="space-y-2">
              <Label htmlFor="eq-nm">Name</Label>
              <Input
                id="eq-nm"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="eq-em">Email</Label>
                <Input
                  id="eq-em"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="eq-ph">Phone</Label>
                <Input
                  id="eq-ph"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="eq-co">Company</Label>
                <Input
                  id="eq-co"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="eq-jt">Job Title</Label>
                <Input
                  id="eq-jt"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                />
              </div>
            </div>

            <h3 className="text-sm font-semibold text-foreground">Enquiry</h3>
            <div className="space-y-2">
              <Label htmlFor="eq-sb">Subject</Label>
              <Input
                id="eq-sb"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="eq-msg">Message</Label>
              <textarea
                id="eq-msg"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className="w-full rounded-md border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(210,100%,50%)]"
                style={{ borderColor: "hsl(210,30%,85%)" }}
              />
            </div>

            <h3 className="text-sm font-semibold text-foreground">Triage</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="eq-pr">Priority</Label>
                <Select
                  id="eq-pr"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                >
                  {priorities.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="eq-ct">Category</Label>
                <Select
                  id="eq-ct"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="">— None —</option>
                  {categories.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="eq-sr">Source</Label>
                <Select
                  id="eq-sr"
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                >
                  {sources.length === 0 && (
                    <option value="manual">Manual</option>
                  )}
                  {sources.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="eq-as">Assigned To</Label>
                <Select
                  id="eq-as"
                  value={assignedToId}
                  onChange={(e) => setAssignedToId(e.target.value)}
                >
                  <option value="">— Unassigned —</option>
                  {users.data?.data.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="eq-pd">Linked Product</Label>
              <Select
                id="eq-pd"
                value={linkedProductId}
                onChange={(e) => setLinkedProductId(e.target.value)}
              >
                <option value="">— None —</option>
                {products.data?.data.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div
            className="flex items-center justify-end gap-3 border-t px-6 py-4"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createEnquiry.isPending}>
              {createEnquiry.isPending && (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" strokeWidth={1.5} />
              )}
              Create Enquiry
            </Button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}
